import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useEvent } from '../store';
import { loadEvent as loadEventFromStorage, saveEvent, calculatePayout, formatCurrency, formatOdds } from '../utils';
import type { Bet, Participant } from '../types';

/**
 * Participant Review Page
 * Admin page for reviewing and manually adjusting participant bet results
 */
export function ParticipantReviewPage() {
  const { eventId, participantId } = useParams<{ eventId: string; participantId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, participants, bets, fights, updateBet, updateParticipant } = useEvent();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!eventId || !participantId) {
      navigate(`/admin/${eventId || ''}`);
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      try {
        const eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          loadEvent(eventData);
        } else {
          setError('Event not found');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        setError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, participantId, loadEvent, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading participant details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">Error</h1>
            <p className="text-secondary-text mb-4">{error}</p>
            <Button variant="secondary" onClick={() => navigate(`/admin/${eventId}`)}>
              Back to Admin Panel
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const participant = participants.find((p) => p.id === participantId);
  if (!participant) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">Participant Not Found</h1>
            <Button variant="secondary" onClick={() => navigate(`/admin/${eventId}`)}>
              Back to Admin Panel
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const participantBets = bets.filter((b) => b.participantId === participantId);

  const getFightName = (fightId: string): string => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight) return 'Unknown Fight';
    return fight.fighters.map((f) => f.name).join(' vs ');
  };

  const getFighterOrOutcomeName = (bet: Bet): string => {
    if (bet.outcomeName) return bet.outcomeName;
    const fight = fights.find((f) => f.id === bet.fightId);
    if (!fight) return 'Unknown Fighter';
    const fighter = fight.fighters.find((f) => f.id === bet.fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const getFightResult = (fightId: string): string | null => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight || !fight.result) return null;
    
    const result = fight.result;
    const winner = fight.fighters.find(f => f.id === result.winnerId);
    const winnerName = winner?.name || 'Unknown';
    
    let methodStr = '';
    switch (result.method) {
      case 'ko_tko': methodStr = 'KO/TKO'; break;
      case 'submission': methodStr = 'Sub'; break;
      case 'decision_unanimous': methodStr = 'UD'; break;
      case 'decision_split': methodStr = 'SD'; break;
      case 'decision_majority': methodStr = 'MD'; break;
      case 'dq': methodStr = 'DQ'; break;
      case 'draw': return 'Draw';
      case 'no_contest': return 'No Contest';
      default: methodStr = result.method;
    }
    
    if (result.wentTheDistance) {
      return `${winnerName} by ${methodStr}`;
    } else if (result.round && result.timeInRound) {
      return `${winnerName} by ${methodStr} (R${result.round}, ${result.timeInRound})`;
    }
    return `${winnerName} by ${methodStr}`;
  };

  const handleToggleBetStatus = (bet: Bet) => {
    if (!event) return;

    const newStatus: Bet['status'] = bet.status === 'won' ? 'lost' : bet.status === 'lost' ? 'won' : 'won';
    
    // Calculate new payout if switching to won
    let newPayout: number | undefined = undefined;
    let balanceChange = 0;

    if (newStatus === 'won') {
      // If switching to won, calculate payout
      if (bet.payout) {
        // Already has payout, use it
        newPayout = bet.payout;
      } else {
        // Calculate new payout
        newPayout = calculatePayout(bet.amount, bet.odds);
      }
      balanceChange = newPayout - (bet.payout || 0);
    } else if (bet.status === 'won') {
      // Switching from won to lost, remove payout
      balanceChange = -(bet.payout || 0);
    }

    // Update bet
    const updatedBet: Bet = {
      ...bet,
      status: newStatus,
      payout: newStatus === 'won' ? newPayout : undefined,
    };

    // Update participant balance
    const updatedParticipant: Participant = {
      ...participant,
      winningsBalance: participant.winningsBalance + balanceChange,
      bets: participant.bets.map((b) => (b.id === bet.id ? updatedBet : b)),
    };

    // Update in store
    updateBet(bet.id, {
      status: newStatus,
      payout: newPayout,
    });

    updateParticipant(participant.id, {
      winningsBalance: updatedParticipant.winningsBalance,
      bets: updatedParticipant.bets,
    });

    // Save to localStorage
    const updatedEvent = {
      ...event,
      participants: event.participants.map((p) =>
        p.id === participant.id ? updatedParticipant : p
      ),
    };
    saveEvent(updatedEvent);
  };

  const statusVariants = {
    pending: 'warning',
    won: 'success',
    lost: 'error',
  } as const;

  const statusLabels = {
    pending: 'Pending',
    won: 'Won',
    lost: 'Lost',
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">{participant.name}</h1>
            <p className="text-sm text-secondary-text">Bet Review & Adjustment</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/admin/${eventId}`)}
          >
            Back to Admin Panel
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Participant Summary */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-primary-text mb-4">Participant Summary</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Total Available</div>
              <div className="text-lg font-bold text-primary-text">
                {formatCurrency(participant.totalAvailable)}
              </div>
            </div>
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Winnings Balance</div>
              <div className="text-lg font-bold text-success-green">
                {formatCurrency(participant.winningsBalance)}
              </div>
            </div>
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Total Bets</div>
              <div className="text-lg font-bold text-primary-text">
                {participantBets.length}
              </div>
            </div>
          </div>
        </Card>

        {/* Bets List */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-primary-text mb-4">All Bets</h2>
          {participantBets.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-text">No bets placed yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {participantBets.map((bet) => {
                const fightName = getFightName(bet.fightId);
                const fighterOrOutcomeName = getFighterOrOutcomeName(bet);

                return (
                  <div
                    key={bet.id}
                    className={`p-4 bg-tertiary-bg rounded-lg border-2 ${
                      bet.status === 'won'
                        ? 'border-success-green bg-success-green/5'
                        : bet.status === 'lost'
                        ? 'border-error/30 bg-error/5'
                        : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-semibold text-lg text-primary-text">
                            {fighterOrOutcomeName}
                          </div>
                          <Badge variant={statusVariants[bet.status]} size="sm">
                            {statusLabels[bet.status]}
                          </Badge>
                        </div>
                        <div className="text-sm text-secondary-text">{fightName}</div>
                        <div className="text-xs text-muted-text mt-1">
                          Placed: {new Date(bet.placedAt).toLocaleString()}
                        </div>
                        {(bet.status === 'won' || bet.status === 'lost') && getFightResult(bet.fightId) && (
                          <div className="text-xs text-muted-text">
                            Result: {getFightResult(bet.fightId)}
                          </div>
                        )}
                      </div>
                      <Button
                        variant={bet.status === 'won' ? 'secondary' : 'primary'}
                        size="sm"
                        onClick={() => handleToggleBetStatus(bet)}
                        disabled={bet.status === 'pending'}
                      >
                        {bet.status === 'won' ? 'Mark as Lost' : bet.status === 'lost' ? 'Mark as Won' : 'Pending'}
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Bet Amount</div>
                        <div className="font-semibold text-primary-text">
                          {formatCurrency(bet.amount)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-secondary-text mb-1">Odds</div>
                        <div className="font-semibold text-primary-text">
                          {formatOdds(bet.odds)}
                        </div>
                      </div>
                      {bet.status === 'won' && bet.payout && (
                        <div>
                          <div className="text-xs text-secondary-text mb-1">Payout</div>
                          <div className="font-semibold text-success-green">
                            {formatCurrency(bet.payout)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Parlay Legs */}
                    {bet.isParlay && bet.parlayLegs && bet.parlayLegs.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="text-sm font-semibold text-primary-text mb-2">Parlay Legs:</div>
                        <div className="space-y-2">
                          {bet.parlayLegs.map((leg, index) => {
                            const legResult = (leg.status === 'won' || leg.status === 'lost') 
                              ? getFightResult(leg.fightId) 
                              : null;
                            return (
                              <div 
                                key={index}
                                className={`p-2 rounded border ${
                                  leg.status === 'won' 
                                    ? 'bg-success-green/10 border-success-green' 
                                    : leg.status === 'lost' 
                                    ? 'bg-error/10 border-error' 
                                    : 'bg-secondary-bg border-border'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-primary-text">{leg.fightName}</div>
                                    <div className="text-xs text-secondary-text mt-1">
                                      {leg.selection} ({leg.odds > 0 ? '+' : ''}{leg.odds})
                                    </div>
                                    {legResult && (
                                      <div className="text-xs text-muted-text mt-1">
                                        Result: {legResult}
                                      </div>
                                    )}
                                  </div>
                                  <div className="ml-2">
                                    {leg.status === 'won' && (
                                      <Badge variant="success" size="sm">✓ Won</Badge>
                                    )}
                                    {leg.status === 'lost' && (
                                      <Badge variant="error" size="sm">✗ Lost</Badge>
                                    )}
                                    {leg.status === 'pending' && (
                                      <Badge variant="warning" size="sm">Pending</Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

